import React, { useState, useEffect, useRef } from 'react';
import { FilterHeader } from './FilterHeader';
import { SearchSection } from './SearchSection';
import { BulkActionsSection } from './BulkActionsSection';
import { ActionsSection } from './ActionsSection';
import type {
  FilterOperator,
  FilterValue,
  FilterOption,
  FilterOperatorObject,
  ColumnDataType,
  AlternateColumnFilter,
} from '../../types';
import {
  ColumnDataTypes,
  FilterOperators,
  isValuelessOperator,
} from '../../types';
import { ColumnValues } from './ColumnValues/ColumnValues';
import { SuggestValuesSection } from './ColumnValues/SuggestValuesSection';
import { ALTERNATE_FILTER_OPERATOR } from './alternateFilter';
import { validateFilterValues } from '../../utils/filterValidation';

export interface ColumnFilterContentProps {
  fieldName: string;
  fieldId: string;
  options: FilterOption[];
  availableOperators: FilterOperatorObject[];
  columnDataType: ColumnDataType;
  range?: {
    min: number;
    max: number;
    step?: number;
  };
  onApply: (filter: FilterValue) => void;
  onCancel?: () => void;
  currentFilter?: FilterValue;
  emptyOptionLabel?: string;
  defaultRender?: (
    option: FilterOption,
    isSelected: boolean
  ) => React.ReactNode;
  onDebouncedSearchChange: (value: string) => void;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
  /** Total number of options available (for dynamic filters, before pagination/filtering) */
  totalOptionsCount?: number;
  /** Optional column description shown in the bulk actions tooltip. */
  description?: string;
  /** KIR-5098: free-text value with a Matches list (see DynamicFilterOptions). */
  suggestInput?: { label: string; placeholder?: string };
  /** KIR-5098: second field picked via the ALTERNATE_FILTER_OPERATOR entry. */
  alternateFilter?: AlternateColumnFilter;
}

export const ColumnFilterContent: React.FC<ColumnFilterContentProps> = ({
  fieldName,
  fieldId,
  options,
  availableOperators,
  columnDataType,
  range,
  onApply,
  onCancel,
  currentFilter,
  emptyOptionLabel = 'None',
  defaultRender,
  onDebouncedSearchChange,
  isLoading = false,
  isError = false,
  onRetry,
  hasMore,
  isFetchingMore,
  onLoadMore,
  totalOptionsCount,
  description,
  suggestInput,
  alternateFilter,
}) => {
  // KIR-5098: an active alternate-field filter reopens on its own entry.
  const operatorOf = (filter?: FilterValue): FilterOperator =>
    alternateFilter && filter?.field === alternateFilter.field
      ? ALTERNATE_FILTER_OPERATOR
      : filter?.operator || availableOperators[0].value;
  const [selectedValues, setSelectedValues] = useState<string[]>(
    currentFilter?.value || []
  );
  const [filterOperator, setFilterOperator] = useState<FilterOperator>(
    operatorOf(currentFilter)
  );
  const isAlternate =
    !!alternateFilter && filterOperator === ALTERNATE_FILTER_OPERATOR;
  // What the inputs, validation and apply act on right now.
  const activeDataType = isAlternate
    ? alternateFilter.dataType
    : columnDataType;
  const activeOperator = isAlternate
    ? alternateFilter.operator
    : filterOperator;
  const [searchQuery, setSearchQuery] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  // True when an operator switch emptied the draft — validate, don't remove.
  const [operatorChangedDraft, setOperatorChangedDraft] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Debounce search query and notify parent
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      onDebouncedSearchChange(searchQuery);
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery, onDebouncedSearchChange]);

  const handleSelectAll = () => {
    // Expand merged values so label-collision options select all wire types.
    setSelectedValues(
      options.flatMap(
        opt =>
          (opt.metadata?.mergedValues as string[] | undefined) ?? [opt.value]
      )
    );
  };

  const handleClearAll = () => {
    setSelectedValues([]);
  };

  const handleValueChange = (value: string) => {
    // When an option carries mergedValues (label-collision dedup), toggle
    // ALL underlying wire types together so the backend filter matches every
    // row that maps to the shared display label.
    const option = options.find(opt => opt.value === value);
    const allValues = (option?.metadata?.mergedValues as
      | string[]
      | undefined) ?? [value];

    setSelectedValues(prev => {
      const isSelected = allValues.some(v => prev.includes(v));
      if (isSelected) {
        return prev.filter(v => !allValues.includes(v));
      }
      return [...prev, ...allValues];
    });
  };

  const handleSingleValueChange = (value: string) => {
    // Single select: replace selection
    setSelectedValues(value ? [value] : []);
  };

  const handleRangeChange = (startDate: string, endDate: string) => {
    // For date ranges, set both values
    setSelectedValues([startDate, endDate]);
  };

  const handleApply = () => {
    const normalizedValues =
      (isTextColumn || suggestInput) && !isAlternate
        ? selectedValues.map(value => value.trim()).filter(Boolean)
        : selectedValues;

    // Emptying an active filter removes it; an operator-emptied draft validates.
    const isClearingActiveFilter =
      selectedValues.length === 0 &&
      (currentFilter?.value.length ?? 0) > 0 &&
      !operatorChangedDraft;

    if (!isClearingActiveFilter) {
      const errors = validateFilterValues(
        activeDataType,
        activeOperator,
        normalizedValues
      );
      if (errors.length > 0 && errors.some(Boolean)) {
        setValidationErrors(errors);
        return;
      }
    }

    setValidationErrors([]);

    onApply({
      field: isAlternate ? alternateFilter.field : fieldId,
      operator: activeOperator,
      value: normalizedValues,
    });
  };

  const handleClear = () => {
    setSelectedValues([]);
    setFilterOperator(availableOperators[0].value);
    setSearchQuery('');
    onDebouncedSearchChange('');
    setValidationErrors([]);
    setOperatorChangedDraft(false);
  };

  const handleCancel = () => {
    // Reset to the current filter state (preserve existing filter)
    setSelectedValues(currentFilter?.value || []);
    setFilterOperator(operatorOf(currentFilter));
    setSearchQuery('');
    onDebouncedSearchChange('');
    setValidationErrors([]);
    setOperatorChangedDraft(false);
    onCancel?.();
  };

  // Check if this is a date/number column (search and bulk actions don't apply)
  const isDateColumn = columnDataType === ColumnDataTypes.DATE;
  const isTextColumn = columnDataType === ColumnDataTypes.TEXT;
  const isNumberColumn = columnDataType === ColumnDataTypes.NUMBER;
  const isStringContainsOperator =
    columnDataType === ColumnDataTypes.STRING &&
    (filterOperator === FilterOperators.CONTAINS ||
      filterOperator === FilterOperators.NOT_CONTAINS);

  const handleOperatorChange = (operator: FilterOperator) => {
    // KIR-5098: the typed / picked endpoint survives Contains ↔ Equals — the
    // operator decides how it matches. Other switches start a fresh draft.
    const keepsSuggestText =
      !!suggestInput &&
      !isAlternate &&
      operator !== ALTERNATE_FILTER_OPERATOR &&
      !isValuelessOperator(operator) &&
      !isValuelessOperator(filterOperator);
    setFilterOperator(operator);
    setValidationErrors([]);
    if (keepsSuggestText) return;
    setSelectedValues([]);
    setSearchQuery('');
    onDebouncedSearchChange('');
    setOperatorChangedDraft(true);

    // Value-less operators need no input — apply immediately so the parent
    // closes the dropdown.
    if (isValuelessOperator(operator)) {
      onApply({ field: fieldId, operator, value: [] });
    }
  };

  const isValuelessSelected = isValuelessOperator(filterOperator);
  const isSingleValueInput =
    isDateColumn || isTextColumn || isStringContainsOperator;

  // KIR-5098: the typed text is both the filter value and the Matches query.
  const handleSuggestValueChange = (value: string) => {
    setSelectedValues(value ? [value] : []);
    setSearchQuery(value.trim());
    // A "value required" error is stale once the user types.
    if (value.trim()) setValidationErrors([]);
  };

  return (
    <div className="flex w-full min-w-0 flex-col gap-2 overflow-x-hidden py-2 bg-popover rounded-md shadow-md">
      <FilterHeader
        fieldName={fieldName}
        filterOperator={filterOperator}
        availableOperators={availableOperators}
        onFilterOperatorChange={handleOperatorChange}
      />

      {!suggestInput &&
        !isAlternate &&
        !isDateColumn &&
        !isTextColumn &&
        !isNumberColumn &&
        !isStringContainsOperator &&
        !isValuelessSelected && (
          <>
            <div className="px-3">
              <SearchSection value={searchQuery} onChange={setSearchQuery} />
            </div>

            <div className="px-3">
              <BulkActionsSection
                onSelectAll={handleSelectAll}
                onClearAll={handleClearAll}
                visibleCount={options.length}
                totalCount={totalOptionsCount ?? options.length}
                description={description}
                fieldName={fieldName}
              />
            </div>
          </>
        )}

      {!isValuelessSelected && (
        <>
          {suggestInput && !isAlternate ? (
            <SuggestValuesSection
              label={suggestInput.label}
              placeholder={suggestInput.placeholder}
              value={selectedValues[0] ?? ''}
              onValueChange={handleSuggestValueChange}
              options={options}
              isLoading={isLoading}
              validationErrors={validationErrors}
              focusKey={filterOperator}
            />
          ) : (
            <ColumnValues
              columnDataType={activeDataType}
              operator={activeOperator}
              options={options}
              selectedValues={selectedValues}
              validationErrors={validationErrors}
              onValueChange={
                isSingleValueInput ? handleSingleValueChange : handleValueChange
              }
              onRangeChange={handleRangeChange}
              range={range}
              emptyOptionLabel={emptyOptionLabel}
              defaultRender={defaultRender}
              isLoading={isLoading}
              isError={isError}
              onRetry={onRetry}
              hasMore={hasMore}
              isFetchingMore={isFetchingMore}
              onLoadMore={onLoadMore}
            />
          )}

          <div className="px-3">
            <ActionsSection
              onApply={handleApply}
              onCancel={handleCancel}
              onClear={handleClear}
              isClearDisabled={selectedValues.length === 0}
            />
          </div>
        </>
      )}
    </div>
  );
};
